import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addActionItemShares,
  createTicketFromTask,
  decideTaskApproval,
  deleteTaskAttachment,
  downloadAttachment,
  getBoard,
  getActionItemCategories,
  getProject,
  getTask,
  listAssignees,
  listDepartments,
  listTaskStatuses,
  moveTask,
  removeActionItemShare,
  requestTaskApproval,
  setTaskTags,
  updateTask,
  uploadTaskAttachment,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useTaskPriorities as usePriorities } from "../lib/priorities";
import { formatBytes, formatDate, formatEstimate, timeAgo } from "../lib/format";
import { TASK_STATUSES } from "../lib/constants";
import type { ActionItemShare, AttachmentRow, CustomFieldValue, TagRow, TaskApprovalRow, TaskCommentRow, TaskRow, UpdateTaskInput } from "../lib/types";
import { Drawer } from "./Drawer";
import { AttachmentThumb } from "./AttachmentThumb";
import { PriorityBadge } from "./Badge";
import { RichTextEditor, RichBody, looksLikeHtml } from "./RichText";
import { TagsEditor } from "./Tags";
import { TaskComments } from "./TaskComments";
import { TaskSubtasks } from "./TaskSubtasks";
import { ApprovalsPanel } from "./ApprovalsPanel";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "./States";

/** A unix-second timestamp → the yyyy-mm-dd in LOCAL time, for an <input type=date>.
 *  Mirrors ProjectDetailPage's dateInputValue so date handling stays consistent. */
function dateInputValue(unixSec?: number | null): string {
  if (!unixSec) return "";
  const d = new Date(unixSec * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** A yyyy-mm-dd <input type=date> string → unix seconds at LOCAL start-of-day
 *  (or null when empty). Inverse of dateInputValue. */
function dateInputToUnix(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  if (!y || !m || !d) return null;
  return Math.floor(new Date(y, m - 1, d).getTime() / 1000);
}

/** Up-to-two-letter initials for an assignee avatar disc. */
function taskInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "?"
  );
}

/** Planner "Repeat" cadences (must match the edge recurrence validation). */
const RECURRENCE_OPTIONS: { value: string; label: string }[] = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export function TaskDrawer({
  taskId,
  onClose,
  onChanged,
  isActionItem = false,
}: {
  taskId: string;
  onClose: () => void;
  onChanged: () => void;
  /** Standalone Action Item (no project): hides board/escalation fields and
   *  relabels the drawer. The data path is identical — both are tasks. */
  isActionItem?: boolean;
}) {
  const { data, loading, error, reload } = useApi((signal) => getTask(taskId, signal), [taskId]);
  const noun = isActionItem ? "Action item" : "Project task";
  // 0090: read-only ONLY when the server explicitly says so (can_edit===false).
  // Missing/undefined/true => editable (back-compat fail-safe-open).
  const readOnly = (data?.can_edit ?? data?.task.can_edit) === false;

  return (
    <Drawer title={noun} onClose={onClose} wide={!!data}>
      {loading ? (
        <Loading label="Loading task…" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : !data ? (
        <p className="muted">{noun} not found.</p>
      ) : (
        <div className="task-2pane">
          {/* Left: task fields, subtasks, escalation. */}
          <div className="task-2pane-main">
            {isActionItem && data.task.created_at ? (
              <div className="sub" style={{ marginBottom: 8 }}>
                Created {formatDate(data.task.created_at)}
                {data.task.created_by_name ? ` · by ${data.task.created_by_name}` : ""}
              </div>
            ) : null}
            <div className="btn-row" style={{ marginBottom: 8 }}>
              <button className="btn small" onClick={() => window.print()} title={`Print this ${noun.toLowerCase()}`}>
                🖨 Print
              </button>
            </div>
            {readOnly ? (
              <Alert kind="info">Shared with you · read-only. You can view this {noun.toLowerCase()} but cannot make changes.</Alert>
            ) : null}
            <TaskForm
              task={data.task}
              fields={data.fields}
              tags={data.tags ?? []}
              assignees={data.assignees ?? []}
              hideProjectFields={isActionItem}
              readOnly={readOnly}
              onSaved={() => {
                reload();
                onChanged();
              }}
            />
            {isActionItem && !readOnly ? (
              <ActionItemShareSection
                taskId={data.task.id}
                initial={data.shares ?? []}
                onChanged={reload}
              />
            ) : null}
            <TaskSubtasks
              parentId={data.task.id}
              projectId={data.task.project_id}
              initial={data.subtasks ?? []}
              readOnly={readOnly}
              onChanged={() => {
                reload();
                onChanged();
              }}
            />
            <TaskAttachments taskId={data.task.id} initial={data.attachments ?? []} readOnly={readOnly} onChanged={reload} />
            <TaskApprovals taskId={data.task.id} approvals={data.approvals ?? []} readOnly={readOnly} onChanged={reload} />
            {!isActionItem && !readOnly ? <EscalateTaskToTicket taskId={data.task.id} onClose={onClose} /> : null}
          </div>
          {/* Right: the task conversation (Planner-style chat pane). */}
          <aside className="task-2pane-aside">
            <TaskComments taskId={data.task.id} initial={data.comments ?? []} teamsLinked={data.teams_linked ?? false} readOnly={readOnly} />
          </aside>
          <TaskPrintSheet
            task={data.task}
            assignees={data.assignees ?? []}
            subtasks={data.subtasks ?? []}
            comments={data.comments ?? []}
            isActionItem={isActionItem}
          />
        </div>
      )}
    </Drawer>
  );
}

/**
 * Read-only, print-optimized rendering of a task / action item. Hidden on screen
 * (display:none) and revealed only by the print stylesheet — mirrors the ticket
 * TicketPrintSheet pattern. Reuses the drawer's already-fetched `data`; resolves
 * status / priority / project labels via the same cached lookups the form uses.
 */
function TaskPrintSheet({
  task,
  assignees,
  subtasks,
  comments,
  isActionItem,
}: {
  task: TaskRow;
  assignees: { user_id: string; name: string }[];
  subtasks: TaskRow[];
  comments: TaskCommentRow[];
  isActionItem: boolean;
}) {
  const priorities = usePriorities();
  const statusesApi = useApi((signal) => listTaskStatuses(signal), []);
  const statusOptions: { code: string; label: string }[] =
    statusesApi.data && statusesApi.data.task_statuses.length > 0
      ? statusesApi.data.task_statuses.map((s) => ({ code: s.code, label: s.name }))
      : TASK_STATUSES;
  // Project name (project tasks only) — resolved via the existing project endpoint.
  const projectApi = useApi(
    (signal) => (!isActionItem && task.project_id ? getProject(task.project_id, signal) : Promise.resolve(null)),
    [isActionItem, task.project_id],
  );
  const projectName = projectApi.data?.project?.name ?? null;

  const statusCode = task.status_code ?? task.status ?? "";
  const statusLabel = statusCode
    ? statusOptions.find((s) => s.code === statusCode)?.label ?? statusCode
    : "—";
  const priorityName = task.priority_id ? priorities.byId(task.priority_id)?.name ?? "—" : "—";
  const assigneeNames = assignees.length
    ? assignees.map((a) => a.name)
    : task.assignee_name
      ? [task.assignee_name]
      : [];

  const rows: [string, string][] = [];
  if (!isActionItem) rows.push(["Project", projectName ?? "—"]);
  rows.push(
    ["Status", statusLabel],
    ["Priority", priorityName],
    ["Assignee(s)", assigneeNames.length ? assigneeNames.join(", ") : "Unassigned"],
    ["Start", task.start_date ? formatDate(task.start_date) : "—"],
    ["Due", task.due_date ? formatDate(task.due_date) : "—"],
  );

  const sortedComments = [...comments].sort((a, b) => a.created_at - b.created_at);

  return (
    <div className="task-print-sheet" aria-hidden="true">
      <header className="tps-head">
        <h1>{task.title}</h1>
      </header>

      <table className="tps-props">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <th>{k}</th>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {task.description ? (
        <>
          <h2 className="tps-section">Description</h2>
          {looksLikeHtml(task.description) ? (
            <div className="tps-comment-body"><RichBody html={task.description} /></div>
          ) : (
            <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{task.description}</div>
          )}
        </>
      ) : null}

      <h2 className="tps-section">Subtasks ({subtasks.length})</h2>
      {subtasks.length === 0 ? (
        <p>No subtasks.</p>
      ) : (
        <ul className="task-print-subtasks">
          {subtasks.map((s) => {
            const sCode = s.status_code ?? s.status ?? "";
            const done = sCode === "done" || sCode === "complete" || sCode === "closed";
            return (
              <li key={s.id}>
                <span className="wo-print-check" aria-hidden="true">{done ? "☑" : "☐"}</span>
                <span className={done ? "wo-print-done" : ""}>{s.title}</span>
              </li>
            );
          })}
        </ul>
      )}

      <h2 className="tps-section">Comments ({sortedComments.length})</h2>
      {sortedComments.length === 0 ? (
        <p>No comments.</p>
      ) : (
        <ol className="tps-comments">
          {sortedComments.map((c) => (
            <li key={c.id} className="tps-comment">
              <div className="tps-comment-meta">
                <strong>{c.author_name ?? "Unknown"}</strong>
                <span>{formatDate(c.created_at)}</span>
              </div>
              {looksLikeHtml(c.body) ? (
                <div className="tps-comment-body"><RichBody html={c.body} /></div>
              ) : (
                <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
              )}
            </li>
          ))}
        </ol>
      )}

      <footer className="tps-foot">Printed {formatDate(Math.floor(Date.now() / 1000))}</footer>
    </div>
  );
}

/** Files attached to a task / action item. Mirrors the ticket & facility
 *  attachment cards: raw-bytes upload, image thumbnails (via AttachmentThumb),
 *  authenticated download, and delete. Local list so the drawer doesn't need a
 *  full reload on every change. */
function TaskAttachments({
  taskId,
  initial,
  onChanged,
  readOnly = false,
}: {
  taskId: string;
  initial: AttachmentRow[];
  onChanged: () => void;
  /** 0090: read-only shared viewer — download only, no upload/remove. */
  readOnly?: boolean;
}) {
  const [items, setItems] = useState<AttachmentRow[]>(initial);
  useEffect(() => setItems(initial), [initial]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { attachment } = await uploadTaskAttachment(taskId, file);
      setItems((prev) => [...prev, attachment]);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function onDownload(id: string, filename: string) {
    try {
      await downloadAttachment(id, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed.");
    }
  }

  async function onRemove(id: string) {
    try {
      await deleteTaskAttachment(taskId, id);
      setItems((prev) => prev.filter((a) => a.id !== id));
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Remove failed.");
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Attachments</h3>
        {readOnly ? null : (
          <label className="btn small" style={{ cursor: uploading ? "wait" : "pointer" }}>
            {uploading ? <Spinner label="Uploading…" /> : "+ Upload"}
            <input type="file" onChange={onPick} disabled={uploading} style={{ display: "none" }} />
          </label>
        )}
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        {items.length === 0 ? (
          <EmptyState>{readOnly ? "No attachments." : "No attachments yet — upload images or docs up to 25 MB."}</EmptyState>
        ) : (
          <ul className="att-list">
            {items.map((a) => (
              <li key={a.id} className="att-item">
                <button
                  type="button"
                  className="att-main"
                  onClick={() => onDownload(a.id, a.filename)}
                  title={`Download ${a.filename}`}
                >
                  <AttachmentThumb att={a} />
                  <span className="att-info">
                    <span className="att-name">{a.filename}</span>
                    <span className="att-sub">
                      {formatBytes(a.size_bytes)}
                      {a.created_at ? ` · ${timeAgo(a.created_at)}` : ""}
                    </span>
                  </span>
                  <span className="att-dl" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
                    </svg>
                  </span>
                </button>
                {readOnly ? null : (
                  <button
                    type="button"
                    className="att-del"
                    onClick={() => onRemove(a.id)}
                    title="Remove attachment"
                    aria-label={`Remove ${a.filename}`}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Cross-module action: turn this task into a helpdesk ticket (inverse of the
 * ticket → task escalation). On success we navigate to the new ticket.
 */
/** Approvals on a task / action item — reuses the shared ApprovalsPanel and the
 *  task approval endpoints (same module as ticket sign-off). */
function TaskApprovals({
  taskId,
  approvals,
  onChanged,
  readOnly = false,
}: {
  taskId: string;
  approvals: TaskApprovalRow[];
  onChanged: () => void;
  /** 0090: read-only shared viewers can't request/decide approvals. */
  readOnly?: boolean;
}) {
  const rosterApi = useApi((signal) => listAssignees(signal), []);
  const roster = rosterApi.data?.assignees ?? [];
  const requestMut = useMutation(requestTaskApproval);
  const decideMut = useMutation(decideTaskApproval);
  // Read-only shared viewers can't manage approvals. Show the panel (status only)
  // when there's something to see, consistent with the rest of the read-only
  // drawer; skip it entirely when there are no approvals (nothing to read).
  if (readOnly && approvals.length === 0) return null;
  return (
    <ApprovalsPanel
      approvals={approvals}
      roster={roster}
      rosterLoading={rosterApi.loading}
      readOnly={readOnly}
      pending={requestMut.pending || decideMut.pending}
      error={requestMut.error || decideMut.error}
      onRequest={async (approverId, note) => {
        const res = await requestMut.run(taskId, { approver_id: approverId, ...(note ? { note } : {}) });
        if (res) onChanged();
        return !!res;
      }}
      onDecide={async (id, approve) => {
        const res = await decideMut.run(id, { approve });
        if (res) onChanged();
        return !!res;
      }}
    />
  );
}

function EscalateTaskToTicket({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { run, pending, error } = useMutation(() => createTicketFromTask(taskId));

  async function escalate() {
    const res = await run();
    if (res) {
      onClose();
      navigate(`/tickets/${res.ticket.id}`);
    }
  }

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        <div className="btn-row">
          <button className="btn" onClick={escalate} disabled={pending}>
            {pending ? <Spinner label="Creating ticket…" /> : "Create ticket from task"}
          </button>
        </div>
        <p className="hint" style={{ marginTop: 8 }}>
          Opens a new helpdesk ticket linked back to this task.
        </p>
      </div>
    </div>
  );
}

function TaskForm({
  task,
  fields,
  tags: initialTags,
  assignees: initialAssignees,
  onSaved,
  hideProjectFields = false,
  readOnly = false,
}: {
  task: TaskRow;
  /** Detail endpoint returns custom-field values as an object (key→value);
   *  tolerate the legacy array shape too. */
  fields: Record<string, unknown> | CustomFieldValue[];
  tags: TagRow[];
  assignees: { user_id: string; name: string }[];
  onSaved: () => void;
  /** Action Items have no board: hide the Bucket control + "show in board". */
  hideProjectFields?: boolean;
  /** 0090: read-only shared viewer — every field disabled, no auto-save. */
  readOnly?: boolean;
}) {
  // Normalize to [label, value] pairs regardless of the wire shape.
  const customFieldEntries: [string, unknown][] = Array.isArray(fields)
    ? fields.map((f) => [f.label || f.key || f.field_id, f.value])
    : Object.entries(fields ?? {});
  const priorities = usePriorities();
  // hideProjectFields is true for Action Items, false for project tasks — drive
  // the role-visibility work-type context off it so each picker filters correctly.
  const assigneesApi = useApi(
    (signal) =>
      listAssignees(signal, {
        workType: hideProjectFields ? "action_items" : "projects",
        field: "assignee",
      }),
    [hideProjectFields],
  );
  const assigneeOptions = assigneesApi.data?.assignees ?? [];
  // Task statuses are configurable (0061); fall back to the TASK_STATUSES
  // constant while the lookup loads or if it fails / returns nothing.
  const statusesApi = useApi((signal) => listTaskStatuses(signal), []);
  const statusOptions: { code: string; label: string }[] =
    statusesApi.data && statusesApi.data.task_statuses.length > 0
      ? statusesApi.data.task_statuses.map((s) => ({ code: s.code, label: s.name }))
      : TASK_STATUSES;
  const departmentsApi = useApi((signal) => listDepartments(signal), []);
  const departmentOptions = departmentsApi.data?.departments ?? [];
  // Category is an Action Item concept (configurable list); only fetch/show it
  // in the action-item context (hideProjectFields).
  const categoriesApi = useApi(
    (signal) => (hideProjectFields ? getActionItemCategories(signal) : Promise.resolve({ categories: [] })),
    [hideProjectFields],
  );
  const categoryOptions = categoriesApi.data?.categories ?? [];
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [status, setStatus] = useState(task.status_code ?? task.status ?? "");
  // Priority is now a ticket_priorities id (built-in field). Fall back to ""
  // (= no priority) when the task has none.
  const [priorityId, setPriorityId] = useState(task.priority_id ?? "");
  const [estimate, setEstimate] = useState(
    task.estimate_minutes != null ? String(task.estimate_minutes) : "",
  );
  const [department, setDepartment] = useState(task.department ?? "");
  const [category, setCategory] = useState(task.category ?? "");
  // Multiple assignees (0033): full ordered set; index 0 is the primary.
  const [assigneeIds, setAssigneeIds] = useState<string[]>(() => initialAssignees.map((a) => a.user_id));
  const [points, setPoints] = useState(task.story_points != null ? String(task.story_points) : "");
  const [startDate, setStartDate] = useState(dateInputValue(task.start_date));
  const [dueDate, setDueDate] = useState(dateInputValue(task.due_date));
  const [recurrence, setRecurrence] = useState(task.recurrence ?? "none");
  const [showInBoard, setShowInBoard] = useState(task.show_in_board == null ? true : task.show_in_board !== 0);
  const [saved, setSaved] = useState(false);

  // Reset local state when the underlying task changes (e.g. after reload).
  useEffect(() => {
    setTitle(task.title);
    setDescription(task.description ?? "");
    setStatus(task.status_code ?? task.status ?? "");
    setPriorityId(task.priority_id ?? "");
    setEstimate(task.estimate_minutes != null ? String(task.estimate_minutes) : "");
    setDepartment(task.department ?? "");
    setCategory(task.category ?? "");
    setAssigneeIds(initialAssignees.map((a) => a.user_id));
    setPoints(task.story_points != null ? String(task.story_points) : "");
    setStartDate(dateInputValue(task.start_date));
    setDueDate(dateInputValue(task.due_date));
    setRecurrence(task.recurrence ?? "none");
    setShowInBoard(task.show_in_board == null ? true : task.show_in_board !== 0);
  }, [task]);

  const { run, pending, error } = useMutation((patch: UpdateTaskInput) =>
    updateTask(task.id, patch),
  );

  // Auto-save like the ticket detail: each field persists on change (selects,
  // dates, toggles, assignees) or on blur (text + rich text), so there's no
  // explicit Save. A brief "Saved" flag confirms each write.
  async function persist(patch: UpdateTaskInput) {
    if (readOnly) return; // defense-in-depth; server also rejects.
    const res = await run(patch);
    if (res) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      onSaved();
    }
  }
  // Persist a new assignee set immediately (used by add/remove).
  function persistAssignees(ids: string[]) {
    setAssigneeIds(ids);
    void persist({ assignee_ids: ids });
  }

  const currentPriorityCode = priorities.byId(priorityId)?.code;

  return (
    <>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <div className="sub" style={{ margin: "0 0 8px", minHeight: 18 }}>
        {readOnly ? <span className="faint">Read-only — you can view but not edit this item.</span> : pending ? <span className="faint">Saving…</span> : saved ? <span className="pill ok">Saved</span> : <span className="faint">Changes save automatically.</span>}
      </div>

      <label className="field">
        <span className="label">Title</span>
        <input
          type="text"
          value={title}
          disabled={readOnly}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title.trim() && title.trim() !== task.title && persist({ title: title.trim() })}
        />
      </label>

      {hideProjectFields && task.created_by_name ? (
        <label className="field">
          <span className="label">Requester</span>
          <input type="text" value={task.created_by_name} readOnly disabled />
        </label>
      ) : null}

      <div
        className="field"
        onBlur={() => description !== (task.description ?? "") && persist({ description })}
      >
        <span className="label">Description</span>
        <RichTextEditor value={description} onChange={setDescription} disabled={readOnly} placeholder="Add details, context, or acceptance criteria…" />
      </div>

      <div className="row">
        <label className="field">
          <span className="label">Status</span>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              if (e.target.value) void persist({ status_code: e.target.value });
            }}
            disabled={readOnly || statusesApi.loading}
          >
            <option value="">—</option>
            {statusOptions.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
            {status && !statusOptions.some((s) => s.code === status) ? (
              <option value={status}>{status}</option>
            ) : null}
          </select>
        </label>
        <label className="field">
          <span className="label">
            Priority{" "}
            {currentPriorityCode ? <PriorityBadge code={currentPriorityCode} /> : null}
          </span>
          <select
            value={priorityId}
            onChange={(e) => {
              setPriorityId(e.target.value);
              void persist({ priority_id: e.target.value ? e.target.value : null });
            }}
            disabled={readOnly || priorities.loading}
          >
            <option value="">—</option>
            {priorities.list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            {priorityId && !priorities.list.some((p) => p.id === priorityId) ? (
              <option value={priorityId}>{priorityId}</option>
            ) : null}
          </select>
        </label>
      </div>

      <div className="row">
        <label className="field">
          <span className="label">
            Estimate (min)
            {estimate.trim() !== "" ? (
              <span className="faint"> · {formatEstimate(Number(estimate))}</span>
            ) : null}
          </span>
          <input
            type="number"
            min={0}
            step={5}
            value={estimate}
            disabled={readOnly}
            onChange={(e) => setEstimate(e.target.value)}
            onBlur={() => {
              const est = estimate.trim();
              const next = est === "" ? null : Math.max(0, Math.round(Number(est)));
              if (next !== (task.estimate_minutes ?? null)) void persist({ estimate_minutes: next });
            }}
            placeholder="—"
          />
        </label>
        <label className="field">
          <span className="label">Department</span>
          <select
            value={department}
            disabled={readOnly || departmentsApi.loading}
            onChange={(e) => {
              setDepartment(e.target.value);
              const next = e.target.value || null;
              if (next !== (task.department ?? null)) void persist({ department: next });
            }}
          >
            <option value="">—</option>
            {departmentOptions.map((d) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
            {department && !departmentOptions.some((d) => d.code === department) ? (
              <option value={department}>{department}</option>
            ) : null}
          </select>
        </label>
        {hideProjectFields ? (
          <label className="field">
            <span className="label">Category</span>
            <select
              value={category}
              disabled={readOnly || categoriesApi.loading}
              onChange={(e) => {
                setCategory(e.target.value);
                const next = e.target.value || null;
                if (next !== (task.category ?? null)) void persist({ category: next });
              }}
            >
              <option value="">—</option>
              {categoryOptions.map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.label}
                </option>
              ))}
              {category && !categoryOptions.some((cat) => cat.code === category) ? (
                <option value={category}>{category}</option>
              ) : null}
            </select>
          </label>
        ) : null}
      </div>

      <div className="field">
        <span className="label">Assignees</span>
        {assigneeIds.length > 0 ? (
          <ul className="person-list">
            {assigneeIds.map((uid, i) => {
              const name =
                assigneeOptions.find((a) => a.id === uid)?.name ??
                initialAssignees.find((a) => a.user_id === uid)?.name ??
                `${uid.slice(0, 8)}…`;
              return (
                <li key={uid} className="person-row">
                  <span className="person-avatar" aria-hidden="true">{taskInitials(name)}</span>
                  <span className="person-main">
                    <span className="person-name">{name}</span>
                  </span>
                  {i === 0 ? <span className="person-primary">Primary</span> : null}
                  {readOnly ? null : (
                    <button
                      type="button"
                      className="person-remove"
                      aria-label={`Remove ${name}`}
                      onClick={() => persistAssignees(assigneeIds.filter((x) => x !== uid))}
                    >
                      ×
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
        {readOnly ? null : (
          <select
            value=""
            disabled={assigneesApi.loading}
            aria-label="Add assignee"
            onChange={(e) => {
              const v = e.target.value;
              if (v && !assigneeIds.includes(v)) persistAssignees([...assigneeIds, v]);
              e.target.value = "";
            }}
          >
            <option value="">+ Add assignee…</option>
            {assigneeOptions
              .filter((a) => !assigneeIds.includes(a.id))
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        )}
        {assigneeIds.length === 0 ? <span className="hint">Unassigned.</span> : null}
      </div>

      {/* Story points are an agile/project-planning concept — hidden for
          standalone action items. */}
      {!hideProjectFields ? (
        <div className="row">
          <label className="field">
            <span className="label">Story points</span>
            <input
              type="number"
              value={points}
              min={0}
              disabled={readOnly}
              onChange={(e) => setPoints(e.target.value)}
              onBlur={() => {
                const next = points.trim() === "" ? null : Number(points);
                if (next !== (task.story_points ?? null)) void persist({ story_points: next });
              }}
              placeholder="—"
            />
          </label>
        </div>
      ) : null}

      <div className="row">
        <label className="field">
          <span className="label">Start date</span>
          <input
            type="date"
            value={startDate}
            disabled={readOnly}
            onChange={(e) => {
              setStartDate(e.target.value);
              void persist({ start_date: dateInputToUnix(e.target.value) });
            }}
          />
        </label>
        <label className="field">
          <span className="label">Due date</span>
          <input
            type="date"
            value={dueDate}
            disabled={readOnly}
            onChange={(e) => {
              setDueDate(e.target.value);
              void persist({ due_date: dateInputToUnix(e.target.value) });
            }}
          />
        </label>
      </div>

      <div className="row">
        <label className="field">
          <span className="label">Repeat</span>
          <select
            value={recurrence}
            disabled={readOnly}
            onChange={(e) => {
              setRecurrence(e.target.value);
              void persist({ recurrence: e.target.value });
            }}
          >
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {!hideProjectFields ? <TaskBucketField task={task} onSaved={onSaved} /> : null}
      </div>

      {!hideProjectFields ? (
        <label className="toggle" style={{ display: "flex", marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={showInBoard}
            onChange={(e) => {
              setShowInBoard(e.target.checked);
              void persist({ show_in_board: e.target.checked ? 1 : 0 });
            }}
          />
          Show in board view
        </label>
      ) : null}

      <TaskTagsEditor taskId={task.id} initial={initialTags} onSaved={onSaved} readOnly={readOnly} />

      {customFieldEntries.length > 0 ? (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-head">
            <h3>Custom fields</h3>
          </div>
          <div className="card-body">
            <dl className="kv">
              {customFieldEntries.map(([key, value]) => (
                <div key={key} style={{ display: "contents" }}>
                  <dt>{key}</dt>
                  <dd>{value == null || value === "" ? "—" : String(value)}</dd>
                </div>
              ))}
            </dl>
            <p className="hint">Custom field editing posts to /api/tasks/:id/fields (later slice).</p>
          </div>
        </div>
      ) : null}

    </>
  );
}

/**
 * Planner "Bucket" = the board column the task sits in. Changing it is a *move*
 * (POST /api/tasks/:id/move), not part of the task patch — so it persists
 * immediately on change rather than waiting for "Save changes". If the task
 * isn't on a board, the field is shown disabled with an explanatory hint.
 */
function TaskBucketField({ task, onSaved }: { task: TaskRow; onSaved: () => void }) {
  const boardId = task.board_id;
  const board = useApi(
    (signal) => (boardId ? getBoard(boardId, signal) : Promise.resolve(null)),
    [boardId],
  );
  const columns = board.data?.columns ?? [];
  const [columnId, setColumnId] = useState(task.column_id ?? "");
  const { run, pending, error } = useMutation((next: string) =>
    moveTask(task.id, { column_id: next }),
  );

  // Re-seed from the task when it reloads.
  useEffect(() => {
    setColumnId(task.column_id ?? "");
  }, [task]);

  async function change(next: string) {
    if (!next || next === task.column_id) {
      setColumnId(next);
      return;
    }
    setColumnId(next);
    const res = await run(next);
    if (res) onSaved();
  }

  if (!boardId) {
    return (
      <label className="field">
        <span className="label">Bucket</span>
        <select disabled>
          <option>Not on a board</option>
        </select>
      </label>
    );
  }

  return (
    <label className="field">
      <span className="label">
        Bucket {pending ? <span className="faint">· moving…</span> : null}
      </span>
      <select
        value={columnId}
        onChange={(e) => change(e.target.value)}
        disabled={board.loading || pending}
      >
        <option value="">—</option>
        {columns.map((col) => (
          <option key={col.id} value={col.id}>
            {col.name}
          </option>
        ))}
        {columnId && !columns.some((col) => col.id === columnId) ? (
          <option value={columnId}>{columnId.slice(0, 8)}… (current)</option>
        ) : null}
      </select>
      {error ? <Alert kind="error">{error}</Alert> : null}
    </label>
  );
}

/**
 * 0090 — Read-only sharing. Visible only to users who can EDIT the action item
 * (creator / assignee / admin). Lets them add tenant users as read-only viewers
 * and revoke existing shares. The current roster comes from the detail response
 * (`detail.shares`) and is kept fresh by re-reading after each change.
 */
function ActionItemShareSection({
  taskId,
  initial,
  onChanged,
}: {
  taskId: string;
  initial: ActionItemShare[];
  onChanged: () => void;
}) {
  const [shares, setShares] = useState<ActionItemShare[]>(initial);
  const rosterApi = useApi((signal) => listAssignees(signal, { workType: "action_items", field: "shared" }), []);
  const roster = rosterApi.data?.assignees ?? [];
  const addMut = useMutation((ids: string[]) => addActionItemShares(taskId, ids));
  const removeMut = useMutation((userId: string) => removeActionItemShare(taskId, userId));

  useEffect(() => setShares(initial), [initial, taskId]);

  const sharedIds = new Set(shares.map((s) => s.user_id));
  const addable = roster.filter((u) => !sharedIds.has(u.id));

  async function add(userId: string) {
    if (!userId) return;
    const res = await addMut.run([userId]);
    if (res) {
      setShares(res.shares);
      onChanged();
    }
  }

  async function remove(userId: string) {
    const res = await removeMut.run(userId);
    if (res) {
      setShares(res.shares);
      onChanged();
    }
  }

  const busy = addMut.pending || removeMut.pending;
  const error = addMut.error || removeMut.error;

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-head">
        <h3>Share (read-only)</h3>
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        <p className="hint" style={{ marginTop: 0 }}>
          Shared people can view this action item (and see it in their My Work) but cannot edit it.
        </p>
        {shares.length > 0 ? (
          <ul className="person-list">
            {shares.map((s) => (
              <li key={s.user_id} className="person-row">
                <span className="person-avatar" aria-hidden="true">{taskInitials(s.name)}</span>
                <span className="person-main">
                  <span className="person-name">{s.name}</span>
                </span>
                <button
                  type="button"
                  className="person-remove"
                  aria-label={`Stop sharing with ${s.name}`}
                  disabled={busy}
                  onClick={() => remove(s.user_id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="hint">Not shared with anyone yet.</p>
        )}
        <select
          value=""
          disabled={rosterApi.loading || busy}
          aria-label="Share with a person"
          onChange={(e) => {
            const v = e.target.value;
            e.target.value = "";
            void add(v);
          }}
        >
          <option value="">+ Share with…</option>
          {addable.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        {busy ? <span className="hint"> · saving…</span> : null}
      </div>
    </div>
  );
}

/** Tag chips persisted independently via the task tag set route (PUT replace). */
function TaskTagsEditor({
  taskId,
  initial,
  onSaved,
  readOnly = false,
}: {
  taskId: string;
  initial: TagRow[];
  onSaved: () => void;
  readOnly?: boolean;
}) {
  const [tags, setTags] = useState<string[]>(initial.map((t) => t.name));
  const { run, pending, error } = useMutation((names: string[]) => setTaskTags(taskId, names));

  // Re-seed from the server set when the task reloads.
  useEffect(() => {
    setTags(initial.map((t) => t.name));
  }, [initial]);

  async function persist(next: string[]) {
    if (readOnly) return;
    setTags(next);
    const res = await run(next);
    if (res) onSaved();
  }

  // Read-only viewers see the tag chips as static text.
  if (readOnly) {
    return (
      <div className="field">
        <span className="label">Tags</span>
        {tags.length ? (
          <div className="tag-chips">
            {tags.map((t) => (
              <span key={t} className="tag-chip">{t}</span>
            ))}
          </div>
        ) : (
          <span className="hint">No tags.</span>
        )}
      </div>
    );
  }

  return (
    <div className="field">
      <span className="label">
        Tags {pending ? <span className="faint">· saving…</span> : null}
      </span>
      <TagsEditor value={tags} onChange={persist} disabled={pending} inputId={`task-tags-${taskId}`} />
      {error ? <Alert kind="error">{error}</Alert> : null}
    </div>
  );
}
